import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { server } from '../../server';
import MyEventsByOwner from './MyEventsByOwner';
import MyEventsProps from './MyEventsProps';
import CreateEventPrompt from './CreateEventPrompt';

const tabs = [
  { name: 'Created Events', key: 'created' },
  { name: 'Attending Events', key: 'attending' },
];

const EventsParentContainer = ({ ownerId }) => {
  const [createdEvents, setCreatedEvents] = useState([]);
  const [attendingEvents, setAttendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('created');
  const { user } = useSelector((state) => state.user);
  const { seller } = useSelector((state) => state.seller);

  const isOwner = Boolean(
    ownerId && (ownerId === user?._id || ownerId === seller?._id)
  );

  useEffect(() => {
    if (!ownerId) {
      setCreatedEvents([]);
      setAttendingEvents([]);
      setError('');
      setLoading(false);
      return;
    }

    fetchAllEvents();
  }, [ownerId]);

  const fetchAllEvents = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${server}/event/user/${ownerId}/events`);
      setCreatedEvents(Array.isArray(response.data.createdEvents) ? response.data.createdEvents : []);
      setAttendingEvents(Array.isArray(response.data.attendingEvents) ? response.data.attendingEvents : []);
    } catch (err) {
      setCreatedEvents([]);
      setAttendingEvents([]);
      setError('Unable to load events right now.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRSVP = async (eventId) => {
    try {
      await axios.delete(`${server}/event/rsvp/${eventId}`, { withCredentials: true });
      const updatedAttendingEvents = attendingEvents.filter((event) => event._id !== eventId);
      setAttendingEvents(updatedAttendingEvents);
    } catch (error) {
      console.error('Failed to cancel RSVP:', error);
      alert('Failed to cancel RSVP: ' + (error.response?.data?.message || 'Error occurred'));
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="events-parent-container">
      <div className="events-parent-container-titlebar">
        <h3>Events</h3>
        <p>Created events and places this profile is going.</p>
      </div>
      <div className="events-parent-container-tabs-border">
        <nav className="events-parent-container-tabs-nav" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={
                activeTab === tab.key
                  ? 'events-parent-container-tabs-button-active'
                  : 'events-parent-container-tabs-button'
              }
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="events-parent-container-tab-content">
        {error && <p className="life-events-error">{error}</p>}
        {activeTab === 'created' && (
          <div className="events-parent-created-events-section">
            {createdEvents.length > 0 ? (
              <MyEventsByOwner events={createdEvents} />
            ) : (
              <CreateEventPrompt
                title="No Events Yet"
                message={
                  isOwner
                    ? 'You have not created any events yet. Create your first one to get started.'
                    : 'This profile has not created any events yet.'
                }
                actionLabel="Create an Event"
                actionHref="/events"
                showAction={isOwner}
              />
            )}
          </div>
        )}
        {activeTab === 'attending' && (
          <div className="events-parent-attending-events-section">
            {attendingEvents.length > 0 ? (
              <MyEventsProps events={attendingEvents} onCancelRSVP={handleCancelRSVP} />
            ) : (
              <CreateEventPrompt
                title="No Events Yet"
                message={
                  isOwner
                    ? 'You are not attending any events yet.'
                    : 'This profile is not attending any events yet.'
                }
                actionLabel="Find Events"
                actionHref="/events"
                showAction={isOwner}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsParentContainer;

